import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Equity",
  "lead": "There has been global progress towards gender parity in education, but gaps remain wide between those in rural and urban areas, and between the rich and the poor."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, IndicatorReference, PageLink, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky, Tooltip} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!PageLink) _missingMdxReference("PageLink", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  if (!Tooltip) _missingMdxReference("Tooltip", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_EQUITY"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "The desire to leave no one behind cuts across the Sustainable Development Goals (SDGs) and is explicit in the fifth target in the education goal, SDG 4. This focus ensures that policy makers prioritise helping the furthest behind first."
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["To be able to monitor whether education gaps are being closed, we need data that is broken down ", _jsx(_components.strong, {
        children: "by income, gender, age, race, ethnicity, migration status, disability, geographic location, and other characteristics"
      }), " relevant to national contexts, which household surveys provide."]
    }), "\n", _jsxs(_components.p, {
      children: ["Since 2010, the ", _jsx(_components.a, {
        href: "https://www.education-inequalities.org/",
        children: "World Inequality Database on Education"
      }), " has been using household surveys to show how wide the education gaps are in different countries. This article summarizes some of the key facts and trends in inequality in education, while some additional aspects are discussed in the article on ", _jsx(PageLink, {
        name: "finance",
        children: "finance"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "gender",
      children: "Gender"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.5.1",
      children: [_jsxs(_components.p, {
        children: ["The graphs below look at the education gaps between groups, such as girls and boys, or the poorest and the richest. It measures that education gap with a ", _jsx(Tooltip, {
          title: "Parity index",
          content: "In a parity index, if the value is close to 0, it shows the disadvantaged group is very far behind; values close to 1 mean there is parity; values close to 2 mean that the advantaged group is the furthest behind. A parity index of 0.5, for instance, could show that there are 30% of girls but 60% of boys enrolled. A parity index of 1.5 would indicate there were 30% of boys but 60% of girls enrolled.",
          children: "parity index"
        }), "."]
      }), _jsxs(_components.p, {
        children: ["Although rarely appreciated, there has been a continuous move towards ", _jsx(Tooltip, {
          title: "Gender parity",
          content: "Where there are equal numbers of boys to girls",
          children: "gender parity"
        }), " in the past generation. Girls and young women in many, mostly richer, parts of the world outnumber boys and young men in schools and universities."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "GPITREND"
          })
        }), _jsx(ScrollySection, {
          id: "income,0",
          children: _jsxs(_components.p, {
            children: ["Any line going below the ", _jsx(HighlightByIndex, {
              index: "-1",
              children: "grey shaded"
            }), " area shows that there are more boys or young men enrolled than girls."]
          })
        }), _jsx(ScrollySection, {
          id: "geo,1",
          children: _jsxs(_components.p, {
            children: ["In ", _jsx(HighlightByIndex, {
              index: "0",
              children: "sub-Saharan Africa"
            }), " there are still fewer than four young women to every five young men attending tertiary education."]
          })
        }), _jsx(ScrollySection, {
          id: "geo,2",
          children: _jsxs(_components.p, {
            children: [_jsx(HighlightByIndex, {
              index: "2",
              children: "Central and Southern Asia"
            }), " has gone\nfrom having girls extremely far behind in 1990, to achieving gender parity in primary to tertiary education,\nmostly thanks to dramatic progress in India. As a result, the region has overtaken ", _jsx(HighlightByIndex, {
              index: "0",
              children: "sub-Saharan Africa"
            }), " in progress in achieving gender parity."]
          })
        })]
      }), _jsx(_components.p, {
        children: "Globally, there has been much progress since 2000. However, drilling down to the country level uncovers persistent gaps: just three in four countries have achieved gender parity in enrolment in primary education, about one in two in lower secondary and one in three in upper secondary education."
      }), _jsxs(_components.p, {
        children: ["The share of countries with a ", _jsx(HighlightByIndex, {
          index: "2",
          children: "large disparity at the expense of girls"
        }), " has at least halved in primary to tertiary education since 2000, but remains 2% in primary, 6% in lower secondary and 9% in upper secondary education."]
      }), _jsxs(_components.p, {
        children: ["We must not forget the boys when we talk about gender equality. While it is less common for countries to have a ", _jsx(HighlightByIndex, {
          index: "0",
          children: "disparity at the expense of boys"
        }), " in primary education, it is far more common in upper secondary education and above."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "PARITYCOUNT"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["In this graph, the greater and darker the blue part of the bar, the more countries there are with ", _jsx(HighlightByIndex, {
              index: "0",
              children: "more boys out of school"
            }), " than girls. The greater and darker the green part of the bar, the more countries there are with ", _jsx(HighlightByIndex, {
              index: "2",
              children: "more girls out of school"
            }), " than boys."]
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["In tertiary education, for instance, three-quarters of countries have ", _jsx(HighlightByIndex, {
              index: "0",
              children: "extreme disparity at the expense of boys"
            }), "."]
          })
        })]
      }), _jsx(_components.h2, {
        id: "wealth",
        children: "Wealth"
      }), _jsx(_components.p, {
        children: "Overall, education gaps between girls and boys are not as wide as between those in rural and urban areas, and between the rich and the poor, which household surveys help reveal."
      }), _jsxs(_components.p, {
        children: ["In Madagascar, for instance, while 85% of the ", _jsx(HighlightByIndex, {
          index: "0",
          children: "richest"
        }), " complete primary education, only 13% of the ", _jsx(HighlightByIndex, {
          index: "1",
          children: "poorest"
        }), " do. In Nigeria, while 94% of the richest complete lower secondary education, only 27% of the poorest do."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "PSECWGAP"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["The red dots show what percentage of the ", _jsx(HighlightByIndex, {
              index: "1",
              children: "poorest"
            }), " children or youth are attending education in comparison to the ", _jsx(HighlightByIndex, {
              index: "0",
              children: "richest"
            }), " – shown in blue. You can change education level from primary to lower secondary education and above at the top of the chart."]
          })
        }), _jsx(ScrollySection, {
          id: "PHL",
          children: _jsxs(_components.p, {
            children: ["In tertiary education, gaps are widest in middle-income countries. For example, in ", _jsx(_components.strong, {
              children: "the Philippines"
            }), ", 61% of the richest but less than 20% of the poorest attended any form of post-secondary education."]
          })
        }), _jsx(ScrollySection, {
          id: "NPL",
          children: _jsxs(_components.p, {
            children: ["In ", _jsx(_components.strong, {
              children: "Nepal"
            }), ", the respective shares were 48% and 5%."]
          })
        })]
      }), _jsx(_components.p, {
        children: "It is even more revealing to look at the way gaps change as children go through their education."
      }), _jsxs(_components.p, {
        children: ["Looking at ", _jsx(_components.strong, {
          children: "gender"
        }), " gaps in ", _jsx(HighlightByIndex, {
          index: "0",
          children: "Burkina Faso"
        }), " below shows the extent to which girls drop out the higher the level of education."]
      }), _jsxs(_components.p, {
        children: ["Alternatively, selecting ", _jsx(_components.strong, {
          children: "wealth"
        }), " shows how much gaps accumulate over the course of the education trajectory at the expense of the poorest, with barely any of them attending past upper secondary."]
      }), _jsx(Figure, {
        id: "WLGPI"
      })]
    }), "\n", _jsx(_components.p, {
      children: "Rarely does a child fall into one single category, meaning that it is crucial to also look at the way that individual characteristics, such as gender, interact with others such as wealth as the next figure does."
    }), "\n", _jsxs(_components.p, {
      children: ["In sub-Saharan Africa, the gaps between the ", _jsx(HighlightByIndex, {
        index: "1",
        children: "poorest"
      }), " and ", _jsx(HighlightByIndex, {
        index: "0",
        children: "richest"
      }), " grow over the course of the education trajectory, with fewer than 35 of the poorest girls, compared to nearly 100 of the richest girls for every 100 boys attending tertiary education."]
    }), "\n", _jsx(_components.p, {
      children: "In Eastern and South-eastern Asia on the other hand, the dots are heading upwards from the grey line because gender gaps are at the expense of the poorest boys. By tertiary education level, the richest girls’ attendance is around 30% higher than their male peers, but the poorest girls’ attendance is 40% higher."
    }), "\n", _jsx(Figure, {
      id: "GPIBYWEALTH"
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "There are unacceptable levels of education inequality across countries and between groups within countries. High-quality disaggregated data is a pre-requisite to designing appropriate strategies to reduce these inequalities and ultimately leave no-one behind in our quest to achieve SDG 4."
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.education-inequalities.org/",
            children: "Worldwide Inequalities Database on Education (WIDE)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.unesco.org/gem-report/en/publication/2025-gender-report",
            children: "Women lead for learning – GEM Gender Report 2025"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.unesco.org/gem-report/en/2024genderreport",
            children: "Technology on her terms – GEM Gender Report 2024"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.savethechildren.org.uk/content/dam/gb/reports/policy/still-left-behind-low-res.pdf",
            children: "Still left behind – Save the Children"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
